<template>
  <JdsSidePanel @close="handleClose">
    <template #header>
      <div>Rate & Review</div>
    </template>

    <template #body>
      <div class="fe-py-m">
        <div class="fe-flex fe-gap-xs fe-mb-base">
          <div>
            <LazyImage
              :src="productData.productImage"
              alt="product image"
              class-name="fe-w-[84px] fe-h-[84px] fe-rounded-xl fe-border fe-border-solid fe-border-primary-grey-40"
            />
          </div>
          <div>
            <div class="fe-text-body-s fe-text-primary-grey-80 fe-pb-xxs">
              {{ productData.productBrand }}
            </div>
            <div class="fe-text-body-s-bold">{{ productData.productName }}</div>
          </div>
        </div>

        <div class="fe-mb-base">
          <div class="fe-text-heading-xxs">Rate your product</div>
          <div>
            <StarRating
              :initial-rating="rating"
              size="xxl"
              @updateRating="handleRatingChange"
            />
          </div>
          <div v-if="ratingError">
            <div
              class="fe-flex fe-items-center fe-text-body-xs fe-text-error-50 fe-py-xs"
            >
              <JdsIcon
                name="ErrorIcon"
                size="m"
                color="fe-text-error-50"
                className="fe-mr-xs"
              />
              {{ ratingError }}
            </div>
          </div>
        </div>

        <div
          class="fe-my-m fe-border-b fe-border-solid fe-border-primary-grey-40"
        />

        <div class="fe-mb-xl" v-if="!reviewConfig?.title?.hidden">
          <JdsInput
            label="Write a Title"
            placeholder="Give a suitable title for your review"
            :min-length="reviewConfig?.title?.minCharLimit"
            :max-length="reviewConfig?.title?.maxCharLimit"
            :value="title"
            @input="handleTitleChange"
            :required="reviewConfig?.title?.required"
            :state="titleError ? 'error' : 'none'"
            :state-text="titleError"
          />
        </div>

        <div v-if="!reviewConfig?.content?.hidden">
          <JdsTextArea
            label="Share a Review"
            placeholder="What would you like other customers to know?"
            :min-length="reviewConfig?.content?.minCharLimit"
            :max-length="reviewConfig?.content?.maxCharLimit"
            :value="content"
            @input="handleContentChange"
            :required="reviewConfig?.content?.required"
            :state="contentError ? 'error' : 'none'"
            :state-text="contentError"
          />
        </div>

        <div
          class="fe-my-m fe-border-b fe-border-solid fe-border-primary-grey-40"
        />

        <div v-if="!reviewConfig.image.hidden">
          <!-- Upload Button Container -->
          <div>
            <div class="fe-text-body-s-bold lg:fe-text-heading-xxs fe-mb-xxs">
              Add photos
              <span class="fe-text-body-xs fe-text-primary-grey-60">
                ({{ reviewConfig.image.required ? "Required" : "Optional" }})
              </span>
            </div>
            <div class="fe-text-body-xs fe-text-primary-grey-80 fe-mb-base">
              Photos make review more helpful.
            </div>
            <div>
              <JdsFileUploadButton
                :allow-multiple-images="!currentVertical.netmeds"
                :accept-file-type="acceptUploadFileType"
                :helper-text="imageHelperText"
                @selected="handleImageSelect"
              >
                Upload images
              </JdsFileUploadButton>
            </div>
          </div>

          <!-- Preview Image Thumbnail Container -->
          <div
            v-if="imageFiles?.length || userImages?.length"
            class="fe-flex fe-items-center fe-w-full fe-overflow-x-auto fe-no-scrollbar"
          >
            <!-- Selected Image Thumbnails -->
            <div class="fe-flex fe-gap-xs fe-py-base">
              <div
                class="fe-relative"
                v-for="(userImage, index) in userImages"
                :key="userImage.fileName"
              >
                <LazyImage
                  :src="userImage.lowResUrl"
                  placeholder="none"
                  :alt="'Uploaded Image ' + (index + 1)"
                  className="fe-w-huge fe-h-huge fe-rounded-medium"
                />
                <div class="fe-absolute fe-top-0 fe-right-0">
                  <JdsIcon
                    name="CloseIcon"
                    size="m"
                    color="fe-text-primary-60"
                    bgColor="fe-bg-primary-20"
                    className="fe-cursor-pointer"
                    @click="handleRemoveUserImage(index)"
                  />
                </div>
              </div>
              <div
                class="fe-relative"
                v-for="(image, index) in imageFiles"
                :key="image.fileName"
              >
                <LazyImage
                  :src="image.imgUrl"
                  placeholder="none"
                  :alt="'Uploaded Image ' + (index + 1)"
                  className="fe-w-huge fe-h-huge fe-rounded-medium"
                />
                <div class="fe-absolute fe-top-[-6px] fe-right-[-6px]">
                  <JdsIcon
                    name="CloseIcon"
                    size="m"
                    color="fe-text-primary-60"
                    bgColor="fe-bg-primary-20"
                    className="fe-cursor-pointer"
                    @click="handleRemoveImage(index)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Image Errors Container -->
          <div
            v-if="imagesError?.length"
            class="fe-flex fe-flex-col fe-bg-primary-grey-20 fe-p-xs fe-rounded-medium"
          >
            <div
              v-for="(error, index) in imagesError"
              :key="index"
              class="fe-flex fe-items-center fe-justify-between fe-text-body-xs fe-text-error-50 fe-py-xs fe-border-b fe-border-solid fe-border-primary-grey-40 last:fe-border-b-0"
            >
              <div class="fe-flex fe-items-center">
                <JdsIcon
                  name="ErrorIcon"
                  size="l"
                  color="fe-text-error-50"
                  className="fe-mr-xs"
                />
                {{ error.message }}
              </div>

              <div>
                <JdsButton
                  @click="handleResetImageError(index)"
                  :iconOnly="true"
                  variant="tertiary"
                >
                  <template #icon>
                    <JdsIcon
                      name="CloseIcon"
                      size="s"
                      color="fe-text-primary-60"
                    />
                  </template>
                </JdsButton>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!currentVertical.netmeds"
          class="fe-my-m fe-border-b fe-border-solid fe-border-primary-grey-40"
        />

        <div
          class="fe-text-body-xxs fe-text-primary-grey-60"
          v-if="!currentVertical.netmeds"
        >
          Your review matters! Find guidance on writing reviews in our
          <JdsLink
            type="link"
            :href="faqLinkUrl"
            className="fe-text-body-xxs-link"
          >
            FAQ section</JdsLink
          >.
        </div>
      </div>
    </template>

    <template #footer>
      <div class="fe-flex fe-justify-between fe-items-center">
        <JdsButton
          size="medium"
          variant="primary"
          :full-width="true"
          :disabled="isSubmitDisabled"
          @click="handleSubmit"
        >
          Submit
        </JdsButton>
      </div>
    </template>
  </JdsSidePanel>
</template>

<script>
  import JdsLink from "@/components/DesignSystem/JdsLink/JdsLink.vue"
  import JdsIcon from "@/components/DesignSystem/JdsIcon/JdsIcon.vue"
  import JdsSidePanel from "@/components/DesignSystem/JdsSidePanel/JdsSidePanel.vue"
  import JdsButton from "@/components/DesignSystem/JdsButton/JdsButton.vue"
  import StarRating from "@/components/DesignSystem/StarRating/StarRating.vue"
  import JdsInput from "@/components/DesignSystem/JdsInput/JdsInput.vue"
  import JdsTextArea from "@/components/DesignSystem/JdsTextArea/JdsTextArea.vue"
  import JdsFileUploadButton from "@/components/DesignSystem/JdsFileUploadButton/JdsFileUploadButton.vue"
  import LazyImage from "@/components/DesignSystem/LazyImage/LazyImage.vue"
  import { toastMixin } from "@/mixins/toastMixin"
  import { modalMixin } from "@/mixins/modalMixin"
  import {
    apiAddReview,
    apiEditReview,
    apiGetSignedImageUrl,
    apiUploadImgToGeneratedSignedUrl,
  } from "@/api_service/customerApi"
  import {
    generateRandomId,
    checkCurrentVerticals,
    isMobileApp,
    dataURItoBlob,
    isAppVersionGreaterThanOrEqual,
  } from "@/utils/utils"
  import { DEFAULT_IMAGE_TYPE_SUPPORT } from "@/constants/constants"
  import GoBackDialog from "./GoBackDialog.vue"

  export default {
    name: "ReviewFormPage",
    components: {
      JdsLink,
      JdsIcon,
      JdsSidePanel,
      JdsButton,
      StarRating,
      JdsInput,
      JdsTextArea,
      JdsFileUploadButton,
      LazyImage,
    },
    inject: ["globalState"],
    mixins: [modalMixin, toastMixin],
    props: {
      selectedRating: {
        type: [Number, String],
        default: 0,
      },
    },
    data() {
      return {
        rating: this.selectedRating,
        title: "",
        content: "",
        imageFiles: [],
        userImages: [],
        // errors
        ratingError: "",
        titleError: "",
        contentError: "",
        imagesError: [],
        loading: false,
      }
    },
    watch: {
      rating() {
        // Check if there's an existing error
        if (this.ratingError) {
          // Validate the new rating and clear the error if valid
          if (this.validateRating()) {
            this.ratingError = ""
          }
        }
      },
      title() {
        // Check if there's an existing error
        if (this.titleError) {
          // Validate the new title and clear the error if valid
          if (this.validateTitleAndContent("title")) {
            this.titleError = ""
          }
        }
      },
      content() {
        if (this.contentError) {
          if (this.validateTitleAndContent("content")) {
            this.contentError = ""
          }
        }
      },
    },
    mounted() {
      if (this.userCurrentReview) {
        this.rating = this.userCurrentReview.rating || 0
        this.title = this.userCurrentReview.title || ""
        this.content = this.userCurrentReview.content || ""
        this.userImages = this.userCurrentReview.images?.slice() || [] // Need immutable array so that global state rating doesn't change if user makes any update
      }

      if (
        isMobileApp() &&
        localStorage.getItem("__application") &&
        isAppVersionGreaterThanOrEqual()
      ) {
        window.FileUploadInterface = {
          sendCapturedImageFromGallery: this.handleNativeImagePicker,
        }
      }
    },
    beforeDestroy() {
      // Freeing up the memory which got created while creating preview image url (No Memory leaks basically)
      this.imageFiles?.forEach((imgFile) =>
        URL.revokeObjectURL(imgFile?.imgUrl),
      )
    },
    computed: {
      currentVertical() {
        return checkCurrentVerticals()
      },
      userCurrentReview() {
        return this.globalState.userCurrentReview
      },
      productData() {
        return this.globalState.product
      },
      reviewConfig() {
        return this.globalState.widgetConfig?.review_properties
      },
      imageHelperText() {
        const imageConfig = this.reviewConfig?.image

        return `Add up to ${
          imageConfig.countLimit
        } images (${DEFAULT_IMAGE_TYPE_SUPPORT?.join(", ")}, max ${
          imageConfig.sizeLimitInMegaBytes
        }MB each)`
      },
      isSubmitDisabled() {
        return (
          !this.rating ||
          this.ratingError ||
          this.titleError ||
          this.contentError ||
          this.imagesError?.length ||
          this.loading
        )
      },
      acceptUploadFileType() {
        return DEFAULT_IMAGE_TYPE_SUPPORT?.map(
          (type) => `image/${type.toLowerCase()}`,
        )
      },
      faqLinkUrl() {
        if (this.currentVertical?.netmeds) {
          return "/c/help-support"
        }
        return "/faq"
      },
    },
    methods: {
      handleClose() {
        this.openDialog(GoBackDialog)
      },
      handleRemoveImage(imageIndex) {
        this.imageFiles.splice(imageIndex, 1)
      },
      handleRemoveUserImage(imageIndex) {
        this.userImages.splice(imageIndex, 1)
      },
      handleResetImageError(errorIndex) {
        // Remove single image error when index is passed
        if (errorIndex >= 0) {
          this.imagesError.splice(errorIndex, 1)
        } else {
          // Remove all image errors
          this.imagesError = []
        }
      },
      handleRatingChange(ratingValue) {
        this.rating = ratingValue
      },
      handleTitleChange(titleValue) {
        this.title = titleValue
      },
      handleContentChange(contentValue) {
        this.content = contentValue
      },
      handleNativeImagePicker(dataURI) {
        const base64String = dataURI.split(",")[1]

        if (base64String) {
          const imageFile = dataURItoBlob(dataURI)

          this.handleImageSelect([imageFile])
        } else {
          this.showToast("Failed to load image")
        }
      },
      handleImageSelect(selectedImages) {
        const selectedImagesArray = Array.from(selectedImages)

        // Check if user has already selected images with same filename etc. Then no need to add these files again.
        const selectedImagesArrayWithDuplicateCheck =
          selectedImagesArray.filter(
            (selectedImage) =>
              !this.imageFiles.some(
                (imageFile) =>
                  imageFile.fileName.slice(7) === selectedImage.name &&
                  imageFile.size === selectedImage.size &&
                  imageFile.contentType === selectedImage.type,
              ),
          )

        // If there is no file after having duplicate check, means user has selected same files again, then no need to do anything, stop right here.
        // We don't need to show any error or warning also to the user in this case.
        if (!selectedImagesArrayWithDuplicateCheck?.length) {
          return
        }

        const selectedImagesArrayWithUrl =
          selectedImagesArrayWithDuplicateCheck.map((imgFile) => {
            // We are adding random 6 character prefix to file name because there is possibility that user may select multiple images with same name.
            // If this happens, our logic written in uploadImgToGeneratedSignedUrl method will fail.
            const updatedImgFileName = `${generateRandomId(6)}_${imgFile.name}`

            // Re-creating this File Object because we need to change the filename in File object too which will get uploaded
            const imgFileData = new File([imgFile], updatedImgFileName, {
              type: imgFile.type,
              lastModified: imgFile.lastModified,
            })

            return {
              fileName: imgFileData.name,
              size: imgFileData.size,
              contentType: imgFileData.type,
              lastModified: imgFileData.lastModified,
              imgUrl: URL.createObjectURL(imgFileData),
              file: imgFileData, // Including the file object itself which will be used in upload api
            }
          })

        // If previously images has been selected, then merge the newly selected images with previous images
        const mergedSelectedImagesArray = this.imageFiles.concat(
          selectedImagesArrayWithUrl,
        )

        this.validateImages(mergedSelectedImagesArray)
      },
      validateImages(selectedImagesArray) {
        const { image: { countLimit, sizeLimitInMegaBytes } = {} } =
          this.reviewConfig

        const maxFileSizeKb = sizeLimitInMegaBytes * 1024

        const imagesErrorArray = []
        const imagesValidFilesArray = []

        // User has already selected images previously, now again selecting more than the limit, so show error
        // User needs to delete the previously selected images first, then only he can select new images
        if (this.userImages?.length + selectedImagesArray.length > countLimit) {
          this.imagesError = [
            { message: `Maximum ${countLimit} images allowed.` },
          ]
          return
        }

        // User has selected more images than the limit, show error
        if (selectedImagesArray.length > countLimit) {
          this.imagesError = [
            { message: `Maximum ${countLimit} images allowed.` },
          ]
          return
        }

        // Helper function to validate image type
        const isValidImageType = (filetype) =>
          this.acceptUploadFileType.includes(filetype)

        // Helper function to validate image size
        const isValidImageSize = (filesize) => filesize / 1024 <= maxFileSizeKb

        selectedImagesArray?.forEach((imageFile) => {
          // User has selected wrong file type other than allowed image extensions
          if (!isValidImageType(imageFile.contentType)) {
            imagesErrorArray.push({
              ...imageFile,
              message: `${imageFile.fileName.slice(
                7,
              )} - Only JPEG, JPG, PNG formats allowed.`,
            })

            return
          }

          // Image size is bigger than max size allowed
          if (!isValidImageSize(imageFile.size)) {
            imagesErrorArray.push({
              ...imageFile,
              message: `${imageFile.fileName.slice(
                7,
              )} - Exceeds max size of ${sizeLimitInMegaBytes}MB.`,
            })

            return
          }

          // All is well, so show the preview image to user in UI
          imagesValidFilesArray.push(imageFile)
        })

        this.imagesError = imagesErrorArray
        this.imageFiles = imagesValidFilesArray
      },
      validateRating() {
        const config = this.reviewConfig?.rating

        // If rating is hidden or not required and empty, then no need to check for validity
        if (config.hidden || (!config.required && !this.rating)) {
          return true
        }

        // If rating is selected by user, then it doesn't matter, if it is required or not, it should met min and max guideline
        if (
          this.rating &&
          this.rating >= config.min &&
          this.rating <= config.max
        ) {
          return true
        }

        const errorMessages = {
          rating: "Rating is required",
          minError: `Minimum ${config.min} rating required`,
          maxError: `Maximum ${config.max} rating required`,
        }

        if (!this.rating) {
          this.ratingError = errorMessages.rating
        } else if (this.rating < config.min) {
          this.ratingError = errorMessages.minError
        } else if (this.rating > config.max) {
          this.ratingError = errorMessages.maxError
        }

        return false
      },
      validateTitleAndContent(type) {
        const config = this.reviewConfig?.[type]

        const charLength = this[type]?.trim().length

        const { minCharLimit = 0, maxCharLimit = 0 } = config

        // If title/content is hidden, then no need to check for validity
        // If title/content string is empty and it is not required (as per config), then no need to check for validity
        if (config.hidden || (!config.required && !charLength)) {
          return true
        }

        // If title/content is filled by user, then it doesn't matter, if it is required or not, it should met min and max character guideline
        if (
          charLength &&
          charLength >= minCharLimit &&
          charLength <= maxCharLimit
        ) {
          return true
        }

        const errorMessages = {
          title: "Please provide a title",
          content: "Please write a review",
          minError: `Minimum ${minCharLimit} characters required`,
          maxError: `Maximum ${minCharLimit} characters required`,
        }

        if (!charLength) {
          this[`${type}Error`] = errorMessages[type]
        } else if (charLength < minCharLimit) {
          this[`${type}Error`] = errorMessages.minError
        } else if (charLength > maxCharLimit) {
          this[`${type}Error`] = errorMessages.maxError
        }

        return false
      },
      // This is being done mainly because we want to execute all methods, so that if there is error in more than 1 methods, then show all corresponding errors
      validateInputBeforeSubmit() {
        const isRatingValid = this.validateRating()
        const isTitleValid = this.validateTitleAndContent("title")
        const isContentValid = this.validateTitleAndContent("content")

        return isRatingValid && isTitleValid && isContentValid
      },

      async getSignedImageUrl() {
        try {
          const requestData = this.imageFiles.map((file) => {
            return {
              fileName: file.fileName,
              size: file.size,
              contentType: file.contentType,
            }
          })

          const response = await apiGetSignedImageUrl(requestData)

          return response?.data || []
        } catch (_error) {
          // No need to show error toast message here because it is being handled in uploadImgToGeneratedSignedUrl
          return []
        }
      },
      async uploadImgToGeneratedSignedUrl(signedUrls) {
        try {
          const uploadImagePromises = signedUrls?.map((url) => {
            const fileObject = this.imageFiles.find(
              (imageFile) => imageFile.fileName === url.originalFileName,
            )

            if (!fileObject) {
              return Promise.reject() // Safeguard in case file object is not found
            }

            const formData = new FormData()

            formData.append("file", fileObject.file)

            return apiUploadImgToGeneratedSignedUrl(url.uploadUrl, formData)
          })

          const uploadImageResults =
            await Promise.allSettled(uploadImagePromises)

          // Return only which are successfully uploaded with corresponding signed url api data (this we need to see if any image not uploaded)
          const successfulUploadedImageData = uploadImageResults.reduce(
            (resultArray, imageResult, currentIndex) => {
              if (imageResult.status === "fulfilled") {
                const imageDataWithSignedUrl = signedUrls[currentIndex]

                resultArray.push({
                  fileName: imageDataWithSignedUrl.uniqueFileName,
                  url: imageDataWithSignedUrl.url,
                })
              }
              return resultArray
            },
            [],
          )

          return successfulUploadedImageData
        } catch (_error) {
          // No need to show error toast message here because it is being handled in handleSubmit
          return []
        }
      },
      async getUploadedImagesUrlList() {
        // NOTE: Here returning undefined in case of any failure because we don't want to send any data in api request in handleSubmit if it is failed

        const numberOfImageToUpload = this.imageFiles?.length || 0

        // Since no images are uploaded, no need to upload anything
        if (!numberOfImageToUpload) {
          return
        }

        const signedUrls = await this.getSignedImageUrl()

        // Image Signed URLs not generated, means images are not going to be uploaded, so returning here itself
        if (!signedUrls?.length) {
          this.showToast(
            `${numberOfImageToUpload} images are not uploaded.`,
            "warning",
          )

          return
        }

        const uploadedImageList =
          await this.uploadImgToGeneratedSignedUrl(signedUrls)

        const failedUploads = numberOfImageToUpload - uploadedImageList?.length

        if (failedUploads) {
          this.showToast(`${failedUploads} images are not uploaded.`)
        }

        // If both failed upload and number of image files to upload are same, means none of the files are uploaded :(
        if (failedUploads === numberOfImageToUpload) {
          return
        }

        return uploadedImageList
      },
      async handleSubmit() {
        try {
          this.loading = true

          if (!this.validateInputBeforeSubmit()) {
            this.loading = false
            return
          }

          const uploadedImagesUrlList = await this.getUploadedImagesUrlList()

          const updatedUserImages = this.userImages?.map((userImage) => {
            return {
              fileName: userImage.fileName,
              url: userImage.url,
            }
          })

          const imagesRequestArray = updatedUserImages.concat(
            uploadedImagesUrlList || [],
          )

          const requestData = {
            productId: this.productData.productId,
            alternateCode1: this.productData.alternateCode1,
            alternateCode2: this.productData.alternateCode2,
            title: this.title.trim(),
            content: this.content.trim(),
            productURL: this.productData.productURL,
            categoryL1: this.productData.categoryL1,
            categoryL2: this.productData.categoryL2,
            categoryL3: this.productData.categoryL3,
            categoryL4: this.productData.categoryL4,
            rating: this.rating,
            images: imagesRequestArray?.length ? imagesRequestArray : undefined,
          }

          let response = null

          if (this.globalState.userReviewed) {
            // If any of the fields has been deleted, then we need to send it in payload to backend.
            const deletedFields = []
            const fieldsToCheck = ["title", "content", "images"]

            fieldsToCheck.forEach((field) => {
              if (
                this.userCurrentReview[field]?.length &&
                !requestData[field]?.length
              ) {
                deletedFields.push(field)
              }
            })

            requestData.deletedFields = deletedFields?.length
              ? deletedFields
              : undefined

            response = await apiEditReview(
              this.userCurrentReview.id,
              requestData,
            )
            requestData.type = "edit_review"

            this.showToast(
              "Thank you for the review. Your review will reflect soon.",
              "success",
            )
          } else {
            response = await apiAddReview(requestData)
            requestData.type = "add_review"
          }

          this.loading = false

          // Extra fields are being added to request data object to send it to Thank you Page, since api after being successful doesn't return any review data
          this.$emit("submit", {
            productBrand: this.productData.productBrand,
            productImage: this.productData.productImage,
            productName: this.productData.productName,
            ...requestData,
          })

          return response?.data
        } catch (error) {
          this.loading = false
          this.showToast(error?.message, "error")
        }
      },
    },
  }
</script>
